import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { getAllUsers, createUser, deleteUser, getTenants } from '@/services/api';
import { Search, User, Shield, Users as UsersIcon, Mail, Phone, Home, Plus, Trash2, UserCog, Building } from 'lucide-react';
import { toast } from 'sonner';

const AdminResidents = () => {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'resident',
    unit_number: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let filtered = [...users, ...tenants.map(t => ({...t, role: 'tenant'}))];
    
    // Filter by role
    if (filterRole !== 'all') {
      filtered = filtered.filter(u => u.role === filterRole);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (u) =>
          u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (u.unit_number && u.unit_number.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredUsers(filtered);
  }, [searchTerm, users, tenants, filterRole]);

  const fetchUsers = async () => {
    try {
      const [usersData, tenantsData] = await Promise.all([
        getAllUsers(),
        getTenants().catch(() => [])
      ]);
      // Filter to only show active non-tenant users (tenants come from getTenants())
      const activeUsers = usersData.filter(u => (u.status === 'active' || !u.status) && u.role !== 'tenant');
      const activeTenants = tenantsData.filter(t => t.status === 'active' || t.role === 'tenant');
      setUsers(activeUsers);
      setTenants(activeTenants);
      setFilteredUsers([...activeUsers, ...activeTenants.map(t => ({...t, role: 'tenant'}))]);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createUser(formData);
      toast.success('User created successfully!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'resident',
        unit_number: ''
      });
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUserId(userId);
      const result = await deleteUser(userId, 'Removed by admin');
      let msg = result.message || 'User removed successfully';
      if (result.cascaded_tenants?.length > 0) {
        msg += ` (${result.cascaded_tenants.length} tenant(s) also removed)`;
      }
      toast.success(msg);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      resident: { variant: 'default', label: 'Resident', icon: User },
      security: { variant: 'secondary', label: 'Security', icon: Shield },
      admin: { variant: 'destructive', label: 'Admin', icon: Shield },
      tenant: { variant: 'outline', label: 'Tenant', icon: Building }
    };
    const item = config[role] || config.resident;
    const Icon = item.icon;
    return (
      <Badge variant={item.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {item.label}
      </Badge>
    );
  };

  const roleStats = {
    total: users.length + tenants.length,
    residents: users.filter(u => u.role === 'resident').length,
    security: users.filter(u => u.role === 'security').length,
    admin: users.filter(u => u.role === 'admin').length,
    tenants: tenants.length
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-residents">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Personnel Management</h1>
          <p className="text-sm md:text-base text-slate-600">Manage residents, security staff, and administrators</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-secondary hover:bg-secondary/90" data-testid="add-resident-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add New User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>Create a new resident, security guard, or admin account</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-name">Full Name</Label>
                <Input
                  id="new-name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="new-user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-email">Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="new-user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-phone">Phone</Label>
                <Input
                  id="new-phone"
                  placeholder="+1 234 567 8900"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="new-user-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-role">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="new-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resident">Resident</SelectItem>
                    <SelectItem value="security">Security Guard</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.role === 'resident' && (
                <div className="space-y-2">
                  <Label htmlFor="new-unit">Unit Number</Label>
                  <Input
                    id="new-unit"
                    placeholder="A-101"
                    value={formData.unit_number}
                    onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                    data-testid="new-user-unit-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="new-password">Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="new-user-password-input"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-secondary hover:bg-secondary/90" data-testid="new-user-submit-btn">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-slate-500 mb-1">Total Personnel</p>
                <p className="text-2xl md:text-3xl font-bold text-purple-700">{roleStats.total}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <UserCog className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-slate-500 mb-1">Residents</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-900">{roleStats.residents}</p>
              </div>
              <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <UsersIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-slate-500 mb-1">Security Staff</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-900">{roleStats.security}</p>
              </div>
              <div className="h-12 w-12 bg-green-50 rounded-xl flex items-center justify-center">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-slate-500 mb-1">Administrators</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-900">{roleStats.admin}</p>
              </div>
              <div className="h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center">
                <Shield className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-orange-50 to-white">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-slate-500 mb-1">Tenants</p>
                <p className="text-2xl md:text-3xl font-bold text-orange-700">{roleStats.tenants}</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Building className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by name, email, or unit number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="resident-search-input"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-role-select">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="resident">Residents</SelectItem>
                <SelectItem value="tenant">Tenants</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="grid gap-4">
        {filteredUsers.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No users found</p>
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map((user) => (
            <Card key={user.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="user-card">
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="h-12 w-12 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-6 w-6 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-base md:text-lg font-semibold text-slate-900">{user.name}</h3>
                        {getRoleBadge(user.role)}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="h-4 w-4 flex-shrink-0" />
                          <span>{user.phone}</span>
                        </div>
                        {user.unit_number && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Home className="h-4 w-4 flex-shrink-0" />
                            <span>Unit {user.unit_number}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        Joined: {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  {/* Delete Button */}
                  <div className="flex justify-end">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          data-testid={`delete-user-btn-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove User</AlertDialogTitle>
                          <AlertDialogDescription asChild>
                            <div className="space-y-3">
                              <p>Are you sure you want to remove <strong>{user.name}</strong>?</p>
                              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                                <p className="text-slate-700"><strong>Email:</strong> {user.email}</p>
                                <p className="text-slate-700"><strong>Phone:</strong> {user.phone || 'N/A'}</p>
                                {user.unit_number && <p className="text-slate-700"><strong>Unit:</strong> {user.unit_number}</p>}
                                <p className="text-slate-700"><strong>Role:</strong> <span className="capitalize">{user.role}</span></p>
                                {user.monthly_rent && <p className="text-slate-700"><strong>Rent:</strong> {'\u20b9'}{Number(user.monthly_rent).toLocaleString('en-IN')}/mo</p>}
                              </div>
                              {user.role === 'resident' && (
                                <p className="text-amber-700 bg-amber-50 p-2 rounded text-sm font-medium">
                                  Removing a resident will also remove all their associated tenants.
                                </p>
                              )}
                              <p className="text-sm text-slate-500">The user will be soft-deleted. Any outstanding dues will remain tracked.</p>
                            </div>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteUser(user.id)}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={deletingUserId === user.id}
                            data-testid={`confirm-delete-btn-${user.id}`}
                          >
                            {deletingUserId === user.id ? 'Removing...' : 'Remove User'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminResidents;
